import {
  App,
  Baustein,
  convertProduktNameToTaasProduktName,
  convertToBffLaufzeit,
  convertToProduktName,
  getEffectiveLaufzeit,
  Leistung,
  Produktlinie as FrontendProduktlinie,
  UnfallLeistung,
  VorvertragVorschaeden,
} from '@ps20-frontend/frontend-models';
import {
  BausteinPreis,
  Feature,
  Laufzeit,
  Produktlinie as TaasProduktlinie,
  Produktlinie as TaasResponseProduktline,
  Produktlinienname,
  SalesProduktVariante,
  TaasTarifrisikoschluesselVWGV2,
  TaasResponseModel,
} from '@ps20/bff-models';
import cloneDeep from 'lodash/cloneDeep';
import { AngebotsId, BasisStreckenState } from '@ps20-frontend/shop-integration-redux';
import { selectFeatureToggle, selectPreisverzeichnisTestValue } from '@ps20-frontend/selector';
import { TaasVertragsoptionen } from './taasVertragsoptionen';
import { getTaasVertragsoptionen } from './taasRequestHelper';

export function updateBausteineWithTarifierungsergebnis(
  state: BasisStreckenState,
  taasResponseProduktlinien: TaasResponseProduktline[],
  appId: App,
): Array<FrontendProduktlinie> {
  const vertragsoptionen = getTaasVertragsoptionen(state.vertragsoptionen, state.produkt.spartenType, {
    aaWorldTkvFT: state.transientState.featureToggles[Feature.AA_WORLD_TKV],
  });

  let newProduktlinien: FrontendProduktlinie[] = cloneDeep(state.produkt.produktlinien);
  newProduktlinien = state.vertragsoptionen?.beibehalten
    ? newProduktlinien.filter(pl => pl.selected)
    : newProduktlinien;

  newProduktlinien.forEach(produktlinie => {
    const salesProduktVariante = getSalesProduktVariante(
      taasResponseProduktlinien,
      produktlinie.produktName,
      vertragsoptionen,
    );

    updateRBDMaxAndPreisverzeichnis(
      taasResponseProduktlinien,
      produktlinie,
      state.vertragsoptionen?.beibehalten,
      state,
    );

    if (!salesProduktVariante) {
      return;
    }

    updateBausteinPreise(produktlinie.zusatzoptionen, salesProduktVariante.bausteinPreise);
    updateBausteinPreise(produktlinie.gefahren, salesProduktVariante.bausteinPreise);
    if (appId === App.UNF) {
      updateBausteinPreise(
        produktlinie.leistungen as UnfallLeistung[],
        salesProduktVariante.bausteinPreise,
      );
    } else if (appId === App.VWG_V2) {
      updateVwgV2HtsLeistungPreis(produktlinie, salesProduktVariante.bausteinPreise);
    } else if (appId === App.RS_VERMIETUNG) {
      produktlinie.zusatzoptionen = [];
      updateBausteinPreiseRsVermietung(
        produktlinie.zusatzoptionen,
        salesProduktVariante.bausteinPreise,
      );
    }

    produktlinie.bruttopreis = salesProduktVariante.linienBruttopreis;
  });
  return newProduktlinien;
}

function updateVwgV2HtsLeistungPreis(
  produktlinie: FrontendProduktlinie,
  bausteinPreise: BausteinPreis<string>[],
): void {
  const htsSchluessel = TaasTarifrisikoschluesselVWGV2.GEBAEUDETECHNIK;
  const htsLeistung = produktlinie.leistungen?.find((l: Leistung) => l.name === htsSchluessel);
  if (!htsLeistung) {
    return;
  }
  const bausteinPreis = bausteinPreise.find(
    bp => (bp.taasTarifrisikoschluessel as unknown as TaasTarifrisikoschluesselVWGV2) === htsSchluessel,
  );
  if (bausteinPreis) {
    htsLeistung.nettopreisProJahr = bausteinPreis.jahresNetto;
    htsLeistung.bruttopreis = bausteinPreis.bruttopreisFuerZahlweise;
    htsLeistung.cpStern = bausteinPreis.cpStern;
  }
}

function updateBausteinPreise<T extends Baustein | UnfallLeistung>(
  bausteine: Array<T>,
  bausteinPreise: BausteinPreis<string>[],
): void {
  bausteine.forEach(baustein => {
    const bausteinPreis = bausteinPreise.find(bs => bs.taasTarifrisikoschluessel === baustein.name);

    if (bausteinPreis) {
      baustein.nettopreisProJahr = bausteinPreis.jahresNetto;
      baustein.bruttopreis = bausteinPreis.bruttopreisFuerZahlweise;
      baustein.cpStern = bausteinPreis.cpStern;
    } else {
      baustein.nettopreisProJahr = 0;
      baustein.bruttopreis = 0;
      baustein.cpStern = 0;
    }
  });
}

export function getLaufzeitForProduktlinie(
  produktname: Produktlinienname,
  lauzeit: number,
): Laufzeit {
  if (Produktlinienname.BASIS !== produktname) {
    return convertToBffLaufzeit(lauzeit);
  } else {
    return Laufzeit.EIN_JAHR;
  }
}

export function updateRBDMaxAndPreisverzeichnis(
  payloadProduktlinien: TaasResponseProduktline[],
  produktlinie: FrontendProduktlinie,
  beibehalten: boolean,
  state: BasisStreckenState,
): void {
  const taasResponseProduktline: TaasResponseProduktline = payloadProduktlinien.find(
    taasRPL => convertToProduktName(taasRPL.salesProductName) === produktlinie.produktName,
  );
  if (!taasResponseProduktline) {
    return;
  }
  if (!beibehalten) {
    produktlinie.rbdMax = taasResponseProduktline.rbdMax;
  }
  produktlinie.preisverzeichnis = taasResponseProduktline.preisverzeichnis;
  if (selectFeatureToggle(state, Feature.PVZ_ERSATZGESCHAEFT_MOCK)) {
    produktlinie.preisverzeichnis = selectPreisverzeichnisTestValue(state);
  }
}

export function getSalesProduktVariante(
  taasResponseProduktlinien: TaasResponseProduktline[],
  produktname: Produktlinienname,
  vertragsoptionen: TaasVertragsoptionen,
): SalesProduktVariante {
  const taasResponseProduktline: TaasResponseProduktline = taasResponseProduktlinien.find(
    taasRPL => convertToProduktName(taasRPL.salesProductName) === produktname,
  );

  return taasResponseProduktline?.salesProduktVariants.find(
    salesProductVariante =>
      salesProductVariante.zahlweise === vertragsoptionen.zahlungsweise &&
      salesProductVariante.selbstbeteiligung === vertragsoptionen.selbstbeteiligung &&
      salesProductVariante.laufzeit ===
        convertToBffLaufzeit(getEffectiveLaufzeit(vertragsoptionen.vertragslaufzeit)),
  );
}

export function filterGesamtpreis(
  actionPayload: TaasResponseModel,
  state: BasisStreckenState,
): number {
  const selectedProduktlinie: FrontendProduktlinie = state.produkt.produktlinien.find(
    produktlinieState => produktlinieState.selected,
  );
  const taasProduktlinie: TaasProduktlinie = actionPayload.produktlinien.find(
    produktlinie =>
      selectedProduktlinie &&
      produktlinie.salesProductName ===
        convertProduktNameToTaasProduktName(selectedProduktlinie.produktName),
  );
  return taasProduktlinie?.salesProduktVariants.find(
    variant =>
      variant.selbstbeteiligung === state.vertragsoptionen.selbstbeteiligung &&
      variant.laufzeit ===
        convertToBffLaufzeit(state.vertragsoptionen.ausgewaehltevertragslaufzeit) &&
      variant.zahlweise === state.vertragsoptionen.zahlungsweise,
  )?.gesamtBruttopreisFuerZahlweise;
}

export function mapVorvertragVorschaeden(
  actionPayload: TaasResponseModel,
  { appId, angebotsId }: AngebotsId,
): VorvertragVorschaeden[] {
  if (actionPayload.vorvertraegeVorschaeden && actionPayload.vorvertraegeVorschaeden.length > 0) {
    const vorvertragVorschaeden: VorvertragVorschaeden[] = [];
    actionPayload.vorvertraegeVorschaeden.forEach(vvVorschaeden =>
      vorvertragVorschaeden.push({
        appId,
        angebotsId,
        anzahlVorschaeden: vvVorschaeden.anzahl,
        vorschaedenArt: vvVorschaeden.vorschadenArt,
      }),
    );
    return vorvertragVorschaeden;
  }
  return null;
}
function updateBausteinPreiseRsVermietung<T>(
  bausteine: Array<T>,
  bausteinPreise: BausteinPreis<string>[],
): void {
  bausteinPreise.forEach(bausteinPreis => {
    const zusatzuOption = {
      info: '',
      isInclusive: true,
      name: 'VERMIETER',
      isSelected: true,
      id: bausteinPreis.id,
      bruttopreis: bausteinPreis.bruttopreisFuerZahlweise,
      nettopreisProJahr: bausteinPreis.jahresNetto,
      fachschluessel: bausteinPreis.taasTarifrisikoschluessel,
    } as unknown as T;
    bausteine.push(zusatzuOption);
  });
}
